import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { Table } from "../../../components/shared";
import useSearchAndFilter from "../../../hooks/useSearchAndFilter";
import FilterSideMenu from "../../../components/shared/FilterSideMenu";
import { AddIcon, BlackDeleteIcon, DownloadIcon, FilterIcon, FilterIconWhite, UploadIcon } from "../../../assets";
import { Badge } from "@mui/material";
import { Loader } from "../../../components/loaders/loaders";
import { columns } from "./MasterZipCodeTypes";
import AddEditZipCodeModal from "./components/AddEditZipCodeModal";
import UploadAgent from "../../../components/modals/UploadAgent";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import { deletePayloadApi } from "../../../apis/api";
import { useMessage } from "../../../context/MessageContext";
import { BULK_DELETE_MASTER_ZIP_CODE, DELETE_MASTER_ZIP_CODE, GET_MASTER_ZIP_CODE_LIST, UPDATE_MASTER_ZIP_CODE } from "../../../apis/end-points";
import { DEFAULT_PAGE_SIZE, DOWNLOAD_MASTER_ZIP_CODE_TEMPLATE_URL } from "../../../utils/constants";
import { GENERAL_ERROR_MESSAGE, SERVICE_CONFIG_MESSAGES } from "../../../utils/message";

const columnsWithCheckbox = columns.map((col) =>
  col.key === "count" ? { ...col, checkbox: true, checkboxId: "id" } : col
);

const MasterZipCode = () => {
  const { setMessage } = useMessage();
  const { countryCode } = useSelector((state: RootState) => state.staticData);
  const countryOptions = countryCode.map((c: any) => ({ label: c.fullName, value: c.fullName }));
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedZipCode, setSelectedZipCode] = useState<any>({});
  const [uploadPopUp, setUploadPopUp] = useState<boolean>(false);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [removeConfirmType, setRemoveConfirmType] = useState<"All" | "Selected" | null>(null);

  const {
    data,
    keyword,
    loading,
    setLoading,
    handleKeywordChange,
    closeOpenfilterModal,
    isShowFilterModal,
    fetchFilteredData,
    fetchWithCurrentFilters,
    captureAppliedFilters,
    clearCurrentFilters,
    filterApplied,
    setFilterApplied,
    city,
    setCity,
    state,
    setState,
    zipCode,
    setZipCode,
    country,
    setCountry,
  } = useSearchAndFilter<any>({
    apiUrl: GET_MASTER_ZIP_CODE_LIST,
    limit: DEFAULT_PAGE_SIZE,
    mapUserData: (data: any[]) => data.map((item: any) => ({
      id: item.id,
      count: item.count,
      zip: item.zipCode || "",
      city: item.city || "",
      state: item.state || "",
      stateCode: item.stateCode || "",
      country: item.country || "",
      countryCode: item.countryCode || "",
      latitude: item.lat || "",
      longitude: item.lng || "",
      active: item.isActive ?? false,
    })),
    screenKey: "masterZipCodeList",
  });

  const handleCloseModal = () => setIsModalOpen(false);

  const handleEdit = (rowData: any) => {
    setIsModalOpen(true);
    setIsEdit(true);
    setSelectedZipCode(rowData);
  };

  const handleClickAdd = () => {
    setIsEdit(false);
    setIsModalOpen(true);
  };

  const handleBulkDelete = async (deleteType: "Selected" | "All") => {
    try {
      setLoading(true);
      const isAllSelected = deleteType === "All";
      const zipCodeId = isAllSelected
        ? data.map((item: any) => item.id).filter(Boolean)
        : checkedIds;

      const response = await deletePayloadApi(BULK_DELETE_MASTER_ZIP_CODE, {
        data: { deleteAll: isAllSelected, zipCodeId },
      });

      if (response.success) {
        setMessage(
          isAllSelected
            ? SERVICE_CONFIG_MESSAGES.ZIP_CODE_BULK_DELETE_ALL_SUCCESS
            : SERVICE_CONFIG_MESSAGES.ZIP_CODE_BULK_DELETE_SELECTED_SUCCESS,
          "success"
        );
        setCheckedIds([]);
        fetchWithCurrentFilters(1);
      } else {
        setMessage(response.error?.message || GENERAL_ERROR_MESSAGE, "error");
      }
    } catch {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  if (isModalOpen) {
    return (
      <AddEditZipCodeModal
        isEdit={isEdit}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        initialData={isEdit ? selectedZipCode : null}
        onSuccess={fetchWithCurrentFilters}
      />
    );
  }

  return (
    <>
    <div className={`tableWrap ${isShowFilterModal ? "tableWrap--filterOpen" : ""}`}>
      <div className="tableWrap__head tableWrap__head--end">
        <div className="btn__group formRow--flexEnd">
          <button
            className="btn btn__white w_fitContent"
            disabled={checkedIds.length === 0}
            onClick={() => setRemoveConfirmType("Selected")}
          >
            <BlackDeleteIcon />
            Remove Selected
          </button>
          <button
            className="btn btn__white w_fitContent"
            disabled={data.length === 0}
            onClick={() => setRemoveConfirmType("All")}
          >
            <BlackDeleteIcon />
            Remove All
          </button>
          <a
            className="btn btn__white w_fitContent"
            target="_blank"
            rel="noreferrer"
            href={DOWNLOAD_MASTER_ZIP_CODE_TEMPLATE_URL}
          >
            <DownloadIcon />
            Download Template
          </a>
          <button
            className="btn btn__white w_fitContent"
            onClick={() => setUploadPopUp(true)}
          >
            <UploadIcon />
            Import
          </button>
          {filterApplied ? (
            <Badge badgeContent=" " color="primary">
              <button
                className={!isShowFilterModal ? "btn btn__white" : "btn btn__primary"}
                onClick={closeOpenfilterModal}
              >
                {!isShowFilterModal ? <FilterIcon /> : <FilterIconWhite />}
                Filter
              </button>
            </Badge>
          ) : (
            <button
              className={!isShowFilterModal ? "btn btn__white" : "btn btn__primary"}
              onClick={closeOpenfilterModal}
            >
              {!isShowFilterModal ? <FilterIcon /> : <FilterIconWhite />}
              Filter
            </button>
          )}

          <FilterSideMenu
            isShowFilterModal={isShowFilterModal}
            closeOpenfilterModal={closeOpenfilterModal}
            isHubHide={true}
            isOrderTypeHide={true}
            isOrderStatusHide={true}
            isStatusHide={true}
            isRoleHide={true}
            isServiceHide={true}
            showCustomerName={false}
            showAgentName={false}
            isAllCustomerTypeHide={true}
            isAgentTypeHide={true}
            showZipCode={true}
            zipcode={zipCode}
            setZipCode={setZipCode}
            showCity={true}
            city={city}
            setCity={setCity}
            showState={true}
            state={state}
            setState={setState}
            showCountry={true}
            country={country}
            setCountry={setCountry}
            countryOptions={countryOptions}
            fetchFilteredData={fetchFilteredData}
            onFiltersApplied={captureAppliedFilters}
            filterApplied={filterApplied}
            setFilterApplied={setFilterApplied}
            table="masterZipCodeListingTable"
            clearFiltersFromStorage={clearCurrentFilters}
          />

          <button className="btn btn__white w_fitContent" onClick={handleClickAdd}>
            <AddIcon />
            Create New
          </button>
        </div>
      </div>

      <Table
        table="masterZipCodeList"
        columns={columnsWithCheckbox}
        setLoading={setLoading}
        data={data}
        getApiData={fetchWithCurrentFilters}
        editIcon={true}
        manageIcon={false}
        onEditNaviagte={{
          apiUrl: "",
          urlID: "id",
          onClick: handleEdit,
          isEditWithoutLink: true,
        }}
        deleteIcon={true}
        onDeleteConfig={{
          apiUrl: DELETE_MASTER_ZIP_CODE,
          urlID: "id",
          successMessage: SERVICE_CONFIG_MESSAGES.ZIP_CODE_DELETE_SUCCESS,
          heading: SERVICE_CONFIG_MESSAGES.ZIP_CODE_DELETE_HEADING,
          rowName: "zip",
          para: SERVICE_CONFIG_MESSAGES.ZIP_CODE_DELETE_CONFIRM,
          deleteByPutApi: false,
        }}
        activeIcon={true}
        onActiveConfig={{
          apiUrl: UPDATE_MASTER_ZIP_CODE,
          urlID: "id",
          successMessage: "Zip code",
          heading: "Zip Code!",
          para: "",
          rowName: "zip",
          secondRowName: "",
          onboardedMessage: "Zip Code",
          notCheckedOnboarded: true,
        }}
        checkedIds={checkedIds}
        setCheckedIds={setCheckedIds}
      />
    </div>
    {uploadPopUp && (
      <UploadAgent
        setConfirmModal={setUploadPopUp}
        popUpTitle="ZipCodes"
        changeStep={fetchWithCurrentFilters}
      />
    )}
    {removeConfirmType && (
      <ConfirmModal
        confirmInfo={{
          heading:
            removeConfirmType === "All"
              ? SERVICE_CONFIG_MESSAGES.ZIP_CODE_BULK_DELETE_ALL_HEADING
              : SERVICE_CONFIG_MESSAGES.ZIP_CODE_BULK_DELETE_SELECTED_HEADING,
          para:
            removeConfirmType === "All"
              ? SERVICE_CONFIG_MESSAGES.ZIP_CODE_BULK_DELETE_ALL_CONFIRM
              : SERVICE_CONFIG_MESSAGES.ZIP_CODE_BULK_DELETE_SELECTED_CONFIRM,
        }}
        onConfirm={() => {
          const type = removeConfirmType;
          setRemoveConfirmType(null);
          handleBulkDelete(type);
        }}
        setConfirmModal={() => setRemoveConfirmType(null)}
        firstMessageShow={true}
      />
    )}
    </>
  );
};

export default MasterZipCode;
